import type {
	ListStreamSummariesResponse,
	ListStreamsResponse,
	Stream,
	StreamField,
	StreamSearchFilters,
	StreamSearchOrder,
	StreamSearchResult,
	StreamSearchSortBy,
	StreamSummary,
} from '@/api/streamsApi'
import { streamsApi } from '@/api/streamsApi'
import { asList } from '@/utils/asList'
import { type PageError, extractErrorMessageAsync, toPageError } from '@/utils/errorHandler'
import { DEVICE_STATE_STREAM } from '@/utils/streamColumns'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { getCacheScope } from './cacheScope'
import { indexedDbStorage, scopedStorage } from './indexedDbStorage'

export type CreateStreamInput = { streamName: string; fields: StreamField[]; ttlDays?: number }

interface StreamsState {
	streams: Stream[]
	/** Tenant the currently cached `streams` list belongs to — undefined means the caller's own tenant. */
	streamsTenantId: string | undefined
	/**
	 * Whether `streams` holds a full catalogue rather than the handful of schemas on-demand
	 * hydration happens to have pulled in. A non-empty list is not proof of the former.
	 */
	streamsListLoaded: boolean
	isLoading: boolean
	error: PageError | null
	createError: string | null
	updateError: string | null
	deleteError: string | null

	streamSummaries: StreamSummary[]
	isLoadingSummaries: boolean
	summariesError: PageError | null

	// Server-driven search/sort/pagination for the list view (POST /v3/streams/search).
	searchResults: StreamSearchResult[]
	searchCount: number
	searchQuery: StreamSearchFilters | null
	searchPage: number
	searchPageSize: number
	searchSortBy: StreamSearchSortBy
	searchOrder: StreamSearchOrder
	isSearching: boolean
	searchError: PageError | null

	fetchStreams: (page?: number, perPage?: number, skipLoading?: boolean, tenantId?: string) => Promise<void>
	fetchStreamSummaries: (page?: number, perPage?: number, tenantId?: string) => Promise<void>
	fetchStream: (streamName: string, silent?: boolean, tenantId?: string) => Promise<Stream | null>
	/** Hydrates several streams' schemas in one request. Returns the names that came back. */
	fetchStreamsByName: (streamNames: string[], tenantId?: string) => Promise<string[]>
	/** Run (or re-run) the current search. Pass `null` to clear filters and show the unfiltered list. */
	searchStreams: (
		query: StreamSearchFilters | null,
		page?: number,
		pageSize?: number,
		sortBy?: StreamSearchSortBy,
		order?: StreamSearchOrder,
		tenantId?: string,
		silent?: boolean,
	) => Promise<void>
	setSearchPage: (page: number, tenantId?: string) => void
	setSearchPageSize: (size: number, tenantId?: string) => void
	setSearchSort: (sortBy: StreamSearchSortBy, order: StreamSearchOrder, tenantId?: string) => void
	createStream: (data: CreateStreamInput, tenantId?: string) => Promise<void>
	createStreamField: (
		streamName: string,
		fieldName: string,
		fieldType: string,
		formatter: string | null,
		tenantId?: string,
	) => Promise<void>
	updateStreamField: (
		streamName: string,
		fieldName: string,
		updates: { type?: string; formatter?: string | null },
		tenantId?: string,
	) => Promise<void>
	deleteStreamField: (streamName: string, fieldName: string, tenantId?: string) => Promise<void>
	deleteStream: (streamName: string, tenantId?: string) => Promise<void>

	setStreams: (streams: Stream[]) => void
	setLoading: (loading: boolean) => void
	setError: (error: PageError | null) => void
	clearError: () => void
	clearCreateError: () => void
	clearUpdateError: () => void
	clearDeleteError: () => void
}

const initialState: Pick<
	StreamsState,
	| 'streams'
	| 'streamsTenantId'
	| 'streamsListLoaded'
	| 'isLoading'
	| 'error'
	| 'createError'
	| 'updateError'
	| 'deleteError'
	| 'streamSummaries'
	| 'isLoadingSummaries'
	| 'summariesError'
	| 'searchResults'
	| 'searchCount'
	| 'searchQuery'
	| 'searchPage'
	| 'searchPageSize'
	| 'searchSortBy'
	| 'searchOrder'
	| 'isSearching'
	| 'searchError'
> = {
	streams: [],
	streamsTenantId: undefined,
	streamsListLoaded: false,
	isLoading: false,
	error: null,
	createError: null,
	updateError: null,
	deleteError: null,
	streamSummaries: [],
	isLoadingSummaries: false,
	summariesError: null,
	searchResults: [],
	searchCount: 0,
	searchQuery: null,
	searchPage: 1,
	searchPageSize: 20,
	searchSortBy: 'name',
	searchOrder: 'asc',
	isSearching: false,
	searchError: null,
}

const isAbortError = (error: unknown): boolean =>
	error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')

/** Latest-wins: a newer search supersedes whatever page/sort/filter request is still in flight. */
let streamSearchAbortController: AbortController | undefined

/**
 * Page size for an unparameterised `fetchStreams()`. A constant, never store state: reading it back
 * from the store meant a bare call fetched whatever size the previous caller happened to leave
 * behind, and since the result *replaces* `streams`, a stale 20 silently truncated the list.
 */
const DEFAULT_PER_PAGE = 1000

/** In-flight `getStream` requests, keyed by tenant+name, so concurrent callers share one request. */
const inFlightStreamFetches = new Map<string, Promise<Stream | null>>()

/**
 * In-flight `getStreams` list requests, keyed by tenant+page+size.
 *
 * Several components prime the catalogue on the same route, a page and a picker it renders being the
 * common pair, and the response replaces `streams` wholesale. Without this the second one is a
 * duplicate of the first, which on a project with many streams is hundreds of KB for nothing.
 */
const inFlightStreamListFetches = new Map<string, Promise<ListStreamsResponse>>()

/**
 * In-flight summary requests, keyed by tenant+page+size. Shared rather than cached: a refresh after a
 * stream is created or deleted asks for the same page, and must issue its own request.
 */
const inFlightStreamSummaryFetches = new Map<string, Promise<ListStreamSummariesResponse>>()

/**
 * The backend lists `device_state` among the streams, but it is synthesised from state keys and every
 * screen that shows it renders its own entry, so the store keeps it out of both lists and the count.
 */

const withoutDeviceState = (query: StreamSearchFilters | null): StreamSearchFilters => {
	const exclude: StreamSearchFilters = { not: { stream_name: { in: [DEVICE_STATE_STREAM] } } }
	return query === null ? exclude : { and: [query, exclude] }
}

/** Carries a freshly fetched stream's retention onto whichever list rows name that stream. */
const withFreshTtl = <T extends { name: string; ttl_days?: number }>(
	rows: T[],
	streamName: string,
	ttlDays: number | undefined,
): T[] => {
	if (typeof ttlDays !== 'number') {
		return rows
	}
	return rows.map(row => (row.name === streamName && row.ttl_days !== ttlDays ? { ...row, ttl_days: ttlDays } : row))
}

export const useStreamsStore = create<StreamsState>()(
	devtools(
		persist(
			subscribeWithSelector((set, get) => ({
				...initialState,

				fetchStreams: async (page?: number, perPage?: number, skipLoading?: boolean, tenantId?: string) => {
					// Set before the dedupe below, so a caller joining an in-flight request still raises its own
					// loading flag and clears it on the shared response.
					if (!skipLoading) {
						set({ isLoading: true, error: null })
					}
					const currentPage = page ?? 1
					const currentPageSize = perPage ?? DEFAULT_PER_PAGE
					const key = `${tenantId ?? ''}:${currentPage}:${currentPageSize}`
					const shared = inFlightStreamListFetches.get(key)
					const request = shared ?? streamsApi.getStreams(currentPage, currentPageSize, tenantId)
					if (!shared) {
						inFlightStreamListFetches.set(key, request)
					}
					try {
						const response = await request

						// Backend may return `{}` (not `[]`) for an empty/no-access list — coerce so `.map` never hits a non-array.
						const streamsList = asList(response.streams)

						set({ streams: streamsList, streamsTenantId: tenantId, streamsListLoaded: true, isLoading: false })
					} catch (error) {
						console.error('Failed to fetch streams:', error)
						set({ error: toPageError(error, 'Failed to load streams'), isLoading: false })
					} finally {
						// Only the caller that registered the request retires it, so a later call refetches
						// rather than joining one that has already settled.
						if (!shared) {
							inFlightStreamListFetches.delete(key)
						}
					}
				},

				fetchStreamSummaries: async (page?: number, perPage?: number, tenantId?: string) => {
					set({ isLoadingSummaries: true, summariesError: null })
					const currentPage = page ?? 1
					const currentPageSize = perPage ?? DEFAULT_PER_PAGE
					const key = `${tenantId ?? ''}:${currentPage}:${currentPageSize}`
					const shared = inFlightStreamSummaryFetches.get(key)
					const request = shared ?? streamsApi.getStreamSummaries(currentPage, currentPageSize, tenantId)
					if (!shared) {
						inFlightStreamSummaryFetches.set(key, request)
					}
					try {
						const response = await request
						const summaries = asList(response.streams).filter(summary => summary.name !== DEVICE_STATE_STREAM)

						set({ streamSummaries: summaries, isLoadingSummaries: false })
					} catch (error) {
						console.error('Failed to fetch stream summaries:', error)
						set({ summariesError: toPageError(error, 'Failed to load streams'), isLoadingSummaries: false })
					} finally {
						// Only the caller that registered it retires it, so a later call refetches rather than
						// joining one that has already settled.
						if (!shared) {
							inFlightStreamSummaryFetches.delete(key)
						}
					}
				},

				fetchStream: async (streamName: string, silent = false, tenantId?: string) => {
					// Callers hydrate on demand from effects that re-run whenever `streams` changes, so the
					// same stream is commonly requested several times before the first response lands.
					// Share the in-flight promise instead of issuing a request per caller.
					const inFlightKey = `${tenantId ?? ''}:${streamName}`
					const inFlight = inFlightStreamFetches.get(inFlightKey)
					if (inFlight) {
						return inFlight
					}

					if (!silent) {
						set({ isLoading: true, error: null })
					}
					const request = (async (): Promise<Stream | null> => {
						try {
							const stream = await streamsApi.getStream(streamName, tenantId)
							// Upsert, not replace-in-place: the list is seeded from lightweight summaries, so
							// the stream being hydrated usually isn't in `streams` yet. Replacing alone would
							// silently drop it and leave callers re-requesting it forever.
							set(state => {
								const index = state.streams.findIndex(s => s.name === streamName)
								return {
									streams:
										index === -1 ? [...state.streams, stream] : state.streams.map((s, i) => (i === index ? stream : s)),
									// The list rows the table renders carry `ttl_days` as well, and only a list refetch
									// would otherwise renew them — so a retention change would read as its old value
									// until a reload. The merge prefers the list row, so leaving it stale hides the
									// schema just fetched.
									streamSummaries: withFreshTtl(state.streamSummaries, streamName, stream.ttl_days),
									searchResults: withFreshTtl(state.searchResults, streamName, stream.ttl_days),
									...(silent ? {} : { isLoading: false }),
								}
							})
							return stream
						} catch (error) {
							console.error(`Failed to fetch stream ${streamName}:`, error)
							// Don't set global error state for individual stream fetch
							if (!silent) {
								set({ isLoading: false })
							}
							return null
						} finally {
							inFlightStreamFetches.delete(inFlightKey)
						}
					})()
					inFlightStreamFetches.set(inFlightKey, request)
					return request
				},

				fetchStreamsByName: async (streamNames: string[], tenantId?: string) => {
					if (streamNames.length === 0) {
						return []
					}
					try {
						// One search beats one `getStream` per name: a CSV can reference dozens of streams.
						const fetched = await streamsApi.getStreamsByName(streamNames, tenantId)

						if (fetched.length > 0) {
							set(state => {
								const byName = new Map(fetched.map(stream => [stream.name, stream]))
								const merged = state.streams.map(stream => byName.get(stream.name) ?? stream)
								const known = new Set(state.streams.map(stream => stream.name))
								return { streams: [...merged, ...fetched.filter(stream => !known.has(stream.name))] }
							})
						}
						return fetched.map(stream => stream.name)
					} catch (error) {
						console.error('Failed to fetch stream schemas:', error)
						return []
					}
				},

				createStream: async (data: CreateStreamInput, tenantId?: string) => {
					set({ error: null })
					try {
						await streamsApi.createStream(data.streamName, data.fields, tenantId, data.ttlDays)
						await get().fetchStreams(1, 1000, true, tenantId)
					} catch (error) {
						console.error('Failed to create stream:', error)
						set({ error: toPageError(error, 'Failed to create stream') })
						throw error
					}
				},

				createStreamField: async (
					streamName: string,
					fieldName: string,
					fieldType: string,
					formatter: string | null,
					tenantId?: string,
				) => {
					set({ createError: null })
					try {
						await streamsApi.createStreamField(streamName, fieldName, fieldType, formatter, tenantId)
						await get().fetchStream(streamName, true, tenantId)
						set({ createError: null })
					} catch (error) {
						console.error('Failed to create stream field:', error)
						const errorMessage = await extractErrorMessageAsync(error, 'Failed to create field')
						set({ createError: errorMessage })
						throw error
					}
				},

				updateStreamField: async (
					streamName: string,
					fieldName: string,
					updates: { type?: string; formatter?: string | null },
					tenantId?: string,
				) => {
					set({ updateError: null })
					try {
						await streamsApi.updateStreamField(streamName, fieldName, updates, tenantId)
						await get().fetchStream(streamName, true, tenantId)
						set({ updateError: null })
					} catch (error) {
						console.error('Failed to update stream field:', error)
						const errorMessage = await extractErrorMessageAsync(error, 'Failed to update field')
						set({ updateError: errorMessage })
						throw error
					}
				},

				deleteStreamField: async (streamName: string, fieldName: string, tenantId?: string) => {
					set({ deleteError: null })
					try {
						await streamsApi.deleteStreamField(streamName, fieldName, tenantId)
						// Refetch the stream rather than patching it locally, so the three field operations
						// all end with the server's version of the schema.
						await get().fetchStream(streamName, true, tenantId)
						set({ deleteError: null })
					} catch (error) {
						console.error('Failed to delete stream field:', error)
						const errorMessage = await extractErrorMessageAsync(error, 'Failed to delete field')
						set({ deleteError: errorMessage })
						throw error
					}
				},

				deleteStream: async (streamName: string, tenantId?: string) => {
					set({ isLoading: true, deleteError: null })
					try {
						await streamsApi.deleteStream(streamName, tenantId)
						set(state => ({
							streams: state.streams.filter(s => s.name !== streamName),
							isLoading: false,
							deleteError: null,
						}))
					} catch (error) {
						console.error('Failed to delete stream:', error)
						const errorMessage = await extractErrorMessageAsync(error, 'Failed to delete stream')
						set({ isLoading: false, deleteError: errorMessage })
						throw error
					}
				},

				searchStreams: async (
					query: StreamSearchFilters | null,
					page?: number,
					pageSize?: number,
					sortBy?: StreamSearchSortBy,
					order?: StreamSearchOrder,
					tenantId?: string,
					silent?: boolean,
				) => {
					const {
						searchPage: currentPage,
						searchPageSize: currentPageSize,
						searchSortBy: currentSortBy,
						searchOrder: currentOrder,
					} = get()
					const effectivePage = page ?? currentPage
					const effectivePageSize = pageSize ?? currentPageSize
					const effectiveSortBy = sortBy ?? currentSortBy
					const effectiveOrder = order ?? currentOrder

					// Latest-wins: abort whatever search request is still in flight.
					streamSearchAbortController?.abort()
					streamSearchAbortController = new AbortController()
					const signal = streamSearchAbortController.signal

					set({
						searchQuery: query,
						searchPage: effectivePage,
						searchPageSize: effectivePageSize,
						searchSortBy: effectiveSortBy,
						searchOrder: effectiveOrder,
						...(silent ? {} : { isSearching: true }),
						searchError: null,
					})

					try {
						const response = await streamsApi.searchStreams(
							{
								filters: withoutDeviceState(query),
								page: effectivePage,
								per_page: effectivePageSize,
								sort_by: effectiveSortBy,
								order: effectiveOrder,
								include_fields: true,
							},
							tenantId,
							signal,
						)

						const totalPages = response.count > 0 ? Math.ceil(response.count / effectivePageSize) : 1
						if (effectivePage > totalPages && response.count > 0) {
							set({ searchPage: totalPages })
							await get().searchStreams(query, totalPages, effectivePageSize, effectiveSortBy, effectiveOrder, tenantId)
							return
						}

						const updatedDepsByName = new Map(response.streams.map(s => [s.name, s.dependency]))
						const currentStreams = get().streams
						const updatedStreams = currentStreams.some(s => updatedDepsByName.has(s.name))
							? currentStreams.map(s => {
									const freshDep = updatedDepsByName.get(s.name)
									return freshDep !== undefined ? { ...s, dependency: freshDep } : s
								})
							: currentStreams

						set({
							searchResults: response.streams,
							searchCount: response.count,
							streams: updatedStreams,
							isSearching: false,
							searchError: null,
						})
					} catch (error) {
						if (isAbortError(error)) {
							return
						}
						console.error('Failed to search streams:', error)
						set({
							isSearching: false,
							searchError: toPageError(error, 'Failed to load streams'),
						})
					}
				},

				setSearchPage: (page: number, tenantId?: string) => {
					if (page === get().searchPage) {
						return
					}
					void get().searchStreams(get().searchQuery, page, undefined, undefined, undefined, tenantId)
				},

				setSearchPageSize: (size: number, tenantId?: string) => {
					void get().searchStreams(get().searchQuery, 1, size, undefined, undefined, tenantId)
				},

				setSearchSort: (sortBy: StreamSearchSortBy, order: StreamSearchOrder, tenantId?: string) => {
					// Sort spans the whole dataset, so jump back to page 1 and refetch from the server.
					void get().searchStreams(get().searchQuery, 1, undefined, sortBy, order, tenantId)
				},

				setStreams: streams => set({ streams }),
				setLoading: loading => set({ isLoading: loading }),
				setError: error => set({ error }),
				clearError: () => set({ error: null }),
				clearCreateError: () => set({ createError: null }),
				clearUpdateError: () => set({ updateError: null }),
				clearDeleteError: () => set({ deleteError: null }),
			})),
			{
				name: 'streams-storage',
				// Scoped per tenant: the cache outlives a project switch, and stream names collide across
				// projects, so one key would rehydrate another project's schemas under the current one.
				storage: createJSONStorage(() => scopedStorage(indexedDbStorage, getCacheScope)),
				// Only the cacheable list data — loading/error flags shouldn't survive a reload.
				partialize: (state: StreamsState) => ({
					streams: state.streams,
					streamsListLoaded: state.streamsListLoaded,
					streamSummaries: state.streamSummaries,
				}),
				// IndexedDB rehydration is asynchronous, so a fetch can land before it resolves. Fill
				// gaps only — the default shallow merge would replace fresh data with the cache.
				merge: (persisted, current): StreamsState => {
					const cached = persisted as
						| Partial<Pick<StreamsState, 'streams' | 'streamsListLoaded' | 'streamSummaries'>>
						| undefined
					const takeCachedStreams = current.streams.length === 0 && Array.isArray(cached?.streams)
					return {
						...current,
						streams: takeCachedStreams ? (cached?.streams ?? []) : current.streams,
						streamsListLoaded: takeCachedStreams ? (cached?.streamsListLoaded ?? false) : current.streamsListLoaded,
						streamSummaries:
							current.streamSummaries.length > 0 || !Array.isArray(cached?.streamSummaries)
								? current.streamSummaries
								: (cached?.streamSummaries ?? []),
					}
				},
			},
		),
	),
)

let hydratedCacheScope: string | null = null

/**
 * Point the persisted cache at the tenant currently in the URL.
 *
 * `persist` reads storage once at store creation, so a project switch (which re-renders rather than
 * reloads) would otherwise keep serving — and keep writing to — the previous tenant's record. Drops
 * the in-memory list first so a mismatched render can't slip through before the new cache arrives.
 * Clearing `streamsTenantId` is deliberate: consumers treat a mismatch as "refetch".
 */
/**
 * Forget which scope is loaded, so the next `resyncStreamsCacheScope` reloads from storage even if
 * the URL scope is unchanged. Needed on sign-out: the next user can land on the same project with
 * different permissions, and the in-memory list would otherwise be carried straight over.
 */
export function forgetStreamsCacheScope(): void {
	hydratedCacheScope = null
	useStreamsStore.setState({ streams: [], streamSummaries: [], streamsTenantId: undefined, streamsListLoaded: false })
}

/**
 * Forget the cached schemas of streams whose fields changed outside this store's own mutations, so
 * the next screen that needs one refetches instead of serving the persisted pre-change version.
 * Summary rows are kept: they carry the server's field count and are what the list renders from.
 *
 * Names are matched across whatever tenant the cache currently holds, since a caller at org level
 * writes to a project the cache may not be scoped to. Dropping a same-named stream from another
 * project costs one refetch and never serves stale fields, which is the safer way round.
 */
export function forgetStreamSchemas(streamNames: string[]): void {
	if (streamNames.length === 0) {
		return
	}
	const stale = new Set(streamNames)
	useStreamsStore.setState(state => {
		const streams = state.streams.filter(stream => !stale.has(stream.name))
		// Same array when nothing matched, so consumers memoized on `streams` don't re-render.
		return streams.length === state.streams.length ? {} : { streams, streamsListLoaded: false }
	})
}

export async function resyncStreamsCacheScope(scope: string): Promise<void> {
	if (hydratedCacheScope === scope) {
		return
	}
	hydratedCacheScope = scope
	useStreamsStore.setState({ streams: [], streamSummaries: [], streamsTenantId: undefined, streamsListLoaded: false })
	await useStreamsStore.persist.rehydrate()
}

export const useStreams = () => useStreamsStore(state => state.streams)

/** Stable identity so mismatched reads don't churn memo consumers. */
const NO_STREAMS: Stream[] = []

/** The loaded list, only if it was fetched for `tenantId`. A mismatch means refetch, not empty. */
export function scopeStreamsToTenant({
	streams,
	streamsTenantId,
	tenantId,
	tenantResolved = true,
}: {
	streams: Stream[]
	streamsTenantId: string | undefined
	tenantId: string | undefined
	/** False when the tenant isn't known yet, so `undefined` doesn't read as project level's own tenant. */
	tenantResolved?: boolean
}): { streams: Stream[]; isMismatched: boolean } {
	const isMismatched = !tenantResolved || streamsTenantId !== tenantId
	return { streams: isMismatched ? NO_STREAMS : streams, isMismatched }
}

/** Hook form of `scopeStreamsToTenant`, reading the store. */
export const useTenantScopedStreams = (
	tenantId: string | undefined,
	tenantResolved = true,
): { streams: Stream[]; isMismatched: boolean } =>
	useStreamsStore(
		useShallow(state =>
			scopeStreamsToTenant({
				streams: Array.isArray(state.streams) ? state.streams : NO_STREAMS,
				streamsTenantId: state.streamsTenantId,
				tenantId,
				tenantResolved,
			}),
		),
	)

// Action selectors - useShallow prevents infinite re-renders when returning objects
export const useStreamsActions = () =>
	useStreamsStore(
		useShallow(state => ({
			fetchStreams: state.fetchStreams,
			fetchStreamSummaries: state.fetchStreamSummaries,
			fetchStream: state.fetchStream,
			fetchStreamsByName: state.fetchStreamsByName,
			createStream: state.createStream,
			createStreamField: state.createStreamField,
			updateStreamField: state.updateStreamField,
			deleteStreamField: state.deleteStreamField,
			deleteStream: state.deleteStream,
			searchStreams: state.searchStreams,
			setSearchPage: state.setSearchPage,
			setSearchPageSize: state.setSearchPageSize,
			setSearchSort: state.setSearchSort,
			setStreams: state.setStreams,
			setLoading: state.setLoading,
			setError: state.setError,
			clearError: state.clearError,
			clearCreateError: state.clearCreateError,
			clearUpdateError: state.clearUpdateError,
			clearDeleteError: state.clearDeleteError,
		})),
	)
